import {Component, computed, inject, input, OnInit, signal, TemplateRef} from '@angular/core';
import {DataService} from "../services/data.service";
import {ToastrService} from "ngx-toastr";
import moment from "moment/moment";
import Swal from "sweetalert2";
import {InvoiceType} from "../interfaces";
import {DatePipe, DecimalPipe} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {TooltipDirective} from "ngx-bootstrap/tooltip";
import {InvoiceTypePipe} from "../pipes/invoice-type.pipe";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {AccessService} from "../services/access.service";

interface InvoiceItem {
    name: string;
    price: number;
    vat: number;
}

interface Invoice {
    id?: number;
    fromType: 'franchisor' | 'franchisee';
    fromID: string;
    toType: 'franchisor' | 'franchisee';
    toID: string;
    invoiceType: InvoiceType;
    header: string;
    date: string;
    period: string;
    period_to: string;
    subject: string;
    items: InvoiceItem[];
}

@Component({
    selector: 'app-invoice-custom',
    imports: [DecimalPipe, FormsModule, TooltipDirective, InvoiceTypePipe, DatePipe],
    template: `
        @let invoiceTypeLabel = labels();

        <h5 class="mb-3">{{ invoiceType() | invoiceType }}</h5>

        @if (!showingInvoice()) {
            <button type="button" class="btn btn-primary"
                    (click)="addInvoice()" [disabled]="loading()">
                <i class="fa-solid fa-file-invoice me-2"></i>{{ invoiceTypeLabel.create }}
            </button>

            <button type="button" class="btn btn-outline-primary ms-2"
                    (click)="openTemplates(templateModal)" [disabled]="loading()">
                <i class="fa-solid fa-layer-group me-2"></i>Vorlagen
            </button>

            @if (invoices.length > 0) {
                <div class="table-responsive">
                    <table class="table table-striped table-hover mt-3 mb-0 pointer">
                        <thead>
                        <tr>
                            <th style="width: 185px">Aktionen</th>
                            <th>Von</th>
                            <th>An</th>
                            <th>Datum</th>
                            <th>Betreff</th>
                            <th>Gesamt (€)</th>
                            <th>{{ invoiceTypeLabel.number }}</th>
                            <th>Erstellt am</th>
                            <th>Versendet am</th>
                            <th>{{ paidName() }}</th>
                        </tr>
                        </thead>
                        <tbody>
                            @for (invoice of invoices; track invoice.id) {
                                <tr>
                                    <td>
                                        @if (invoice.sent_at == null) {
                                            <button type="button" class="btn btn-sm btn-outline-danger me-2" [tooltip]="invoiceTypeLabel.delete" (click)="deleteInvoice(invoice)" [disabled]="loading()">
                                                <i class="fa-solid fa-trash"></i>
                                            </button>
                                        }
                                        <button type="button" class="btn btn-sm btn-outline-primary me-2" [tooltip]="invoiceTypeLabel.edit" (click)="editInvoice(invoice)" [disabled]="loading()">
                                            <i class="fa-solid fa-pencil"></i>
                                        </button>
                                        <button type="button" class="btn btn-sm btn-outline-primary me-2" [tooltip]="invoiceTypeLabel.send" (click)="sendInvoice(invoice)" [disabled]="loading()">
                                            <i class="fa-solid fa-envelope"></i>
                                        </button>
                                        <button type="button" class="btn btn-sm btn-outline-primary" [tooltip]="invoiceTypeLabel.show" (click)="createInvoice(invoice, pdfModal)" [disabled]="loading()">
                                            <i class="fa-solid fa-file-invoice"></i>
                                        </button>
                                    </td>
                                    <td>
                                        {{ invoice.from_name }}
                                    </td>
                                    <td>
                                        {{ invoice.to_name }}
                                    </td>
                                    <td>
                                        {{ invoice.date | date: 'dd.MM.yyyy' }}
                                    </td>
                                    <td>
                                        {{ invoice.subject }}
                                    </td>
                                    <td>
                                        {{ invoice.total | number: '1.2-2'}}
                                    </td>
                                    <td>
                                        {{ invoice.invoice_number }}
                                    </td>
                                    <td>
                                        {{ invoice.created_at | date: 'dd.MM.yyyy' }}
                                    </td>
                                    <td>
                                        {{ invoice.sent_at | date: 'dd.MM.yyyy HH:mm' }}
                                        @if (invoice.sent_at == null) {
                                            <span class="text-muted">Noch nicht versendet</span>
                                        }
                                    </td>
                                    <td>
                                        {{ invoice.paid_at | date: 'dd.MM.yyyy' }}
                                        @if (invoice.paid_at == null) {
                                            <span class="text-muted me-2">Noch nicht {{ this.invoiceType() === InvoiceType.CREDIT ? 'gezahlt ' : 'bezahlt' }}</span>
                                            <button type="button" class="btn btn-sm btn-outline-primary" tooltip="Datum erfassen" [disabled]="loading()" (click)="setPaidDate(invoice)">
                                                <i class="fa-solid fa-money-bill"></i>
                                            </button>
                                        }
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            } @else {
                <div class="mt-3">{{ invoiceTypeLabel.none }}</div>
            }
        } @else if (currentInvoice) {
            <div class="card p-3 mt-3">
                <form #newInvoiceForm="ngForm" (ngSubmit)="saveInvoice()">
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Absender</label>
                            <div class="mb-2">
                                <div class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" name="fromType" id="fromFranchisor" value="franchisor" [(ngModel)]="currentInvoice.fromType" (ngModelChange)="resetFromID()">
                                    <label class="form-check-label" for="fromFranchisor">Franchisegeber</label>
                                </div>
                                <div class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" name="fromType" id="fromFranchisee" value="franchisee" [(ngModel)]="currentInvoice.fromType" (ngModelChange)="resetFromID()">
                                    <label class="form-check-label" for="fromFranchisee">Franchisenehmer</label>
                                </div>
                            </div>
                            @if (currentInvoice.fromType === 'franchisor') {
                                <select class="form-select" name="fromId" [(ngModel)]="currentInvoice.fromID" (ngModelChange)="loadInvoiceType()" required>
                                    <option value="" disabled selected style="display: none;">Bitte wählen...</option>
                                    @for (f of franchisorsFrom; track f.id) {
                                        <option [value]="f.id">{{ f.name }}</option>
                                    }
                                </select>
                            } @else {
                                <select class="form-select" name="fromId" [(ngModel)]="currentInvoice.fromID" (ngModelChange)="loadInvoiceType()" required>
                                    <option value="" disabled selected style="display: none;">Bitte wählen...</option>
                                    @for (f of franchiseesFrom; track f.id) {
                                        <option [value]="f.id">{{ f.name }}</option>
                                    }
                                </select>
                            }
                        </div>

                        <div class="col-md-6 mb-3">
                            <label class="form-label">Empfänger</label>
                            <div class="mb-2">
                                <div class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" name="toType" id="toFranchisor" value="franchisor" [(ngModel)]="currentInvoice.toType" (ngModelChange)="resetToID()">
                                    <label class="form-check-label" for="toFranchisor">Franchisegeber</label>
                                </div>
                                <div class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" name="toType" id="toFranchisee" value="franchisee" [(ngModel)]="currentInvoice.toType" (ngModelChange)="resetToID()">
                                    <label class="form-check-label" for="toFranchisee">Franchisenehmer</label>
                                </div>
                            </div>
                            @if (currentInvoice.toType === 'franchisor') {
                                <select class="form-select" name="toId" [(ngModel)]="currentInvoice.toID" required>
                                    <option value="" disabled selected style="display: none;">Bitte wählen...</option>
                                    @for (f of franchisors; track f.id) {
                                        <option [value]="f.id">{{ f.name }}</option>
                                    }
                                </select>
                            } @else {
                                <select class="form-select" name="toId" [(ngModel)]="currentInvoice.toID" required>
                                    <option value="" disabled selected style="display: none;">Bitte wählen...</option>
                                    @for (f of franchisees; track f.id) {
                                        <option [value]="f.id">{{ f.name }}</option>
                                    }
                                </select>
                            }
                        </div>
                    </div>

                    <div class="row">
                        <div class="col mb-3">
                            <label class="form-label">{{ invoiceTypeLabel.header }}</label>
                            <input type="text" class="form-control" name="header" [(ngModel)]="currentInvoice.header" required>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-2 mb-3">
                            <label class="form-label">{{ invoiceTypeLabel.date }}</label>
                            <input type="date" class="form-control" name="date" [(ngModel)]="currentInvoice.date" required>
                        </div>
                        <div class="col-md-2 mb-3">
                            <label class="form-label">Leistungszeitraum Von</label>
                            <input type="date" class="form-control" name="period" [(ngModel)]="currentInvoice.period" required>
                        </div>
                        <div class="col-md-2 mb-3">
                            <label class="form-label">Leistungszeitraum Bis</label>
                            <input type="date" class="form-control" name="period_to" [(ngModel)]="currentInvoice.period_to">
                        </div>
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Betreff</label>
                            <input type="text" class="form-control" name="subject" [(ngModel)]="currentInvoice.subject" required>
                        </div>
                    </div>

                    <hr>
                    <h6>{{ invoiceTypeLabel.items }}</h6>
                    <div class="table-responsive">
                        <table class="table table-sm">
                            <thead>
                            <tr>
                                <th>Bezeichnung</th>
                                <th style="width: 150px">Preis (Netto)</th>
                                <th style="width: 100px">MwSt (%)</th>
                                <th style="width: 150px">Steuerbetrag</th>
                                <th style="width: 150px">Bruttobetrag</th>
                                <th style="width: 100px">Währung</th>
                                <th style="width: 50px"></th>
                            </tr>
                            </thead>
                            <tbody>
                                @for (item of currentInvoice.items; track $index) {
                                    <tr>
                                        <td>
                                            <input type="text" class="form-control form-control-sm" [name]="'item_name_' + $index" [(ngModel)]="item.name" required>
                                        </td>
                                        <td>
                                            <input type="number" class="form-control form-control-sm text-end" [name]="'item_price_' + $index" [(ngModel)]="item.price" required step="0.01" min="0">
                                        </td>
                                        <td>
                                            <select class="form-select form-select-sm text-end" [name]="'item_vat_' + $index" [(ngModel)]="item.vat" required>
                                                <option [value]="0">0</option>
                                                <option [value]="20">20</option>
                                            </select>
                                        </td>
                                        <td class="text-end align-middle">
                                            {{ ((item.price || 0) * (item.vat || 0) / 100) | number:'1.2-2' }} €
                                        </td>
                                        <td class="text-end align-middle">
                                            {{ ((item.price || 0) * (1 + (item.vat || 0) / 100)) | number:'1.2-2' }} €
                                        </td>
                                        <td class="text-center align-middle">
                                            EUR
                                        </td>
                                        <td>
                                            <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeItem($index)" [disabled]="$index === 0">
                                                <i class="fa-solid fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                            <tfoot>
                            <tr class="fw-bold">
                                <td>Gesamt</td>
                                <td class="text-end">{{ totalNet | number:'1.2-2' }} €</td>
                                <td></td>
                                <td class="text-end">{{ totalVat | number:'1.2-2' }} €</td>
                                <td class="text-end">{{ totalGross | number:'1.2-2' }} €</td>
                                <td class="text-center">EUR</td>
                                <td></td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-primary mb-3" (click)="addItem()">
                        <i class="fa-solid fa-plus me-1"></i>Position hinzufügen
                    </button>

                    <div class="d-flex justify-content-end gap-2">
                        <button type="button" class="btn btn-outline-primary" (click)="saveTemplate()" [disabled]="!newInvoiceForm.form.valid">
                            <i class="fa-solid fa-save me-1"></i>Als Vorlage speichern
                        </button>
                        <button type="button" class="btn btn-secondary" (click)="cancelAddInvoice()">Abbrechen</button>
                        <button type="submit" class="btn btn-primary" [disabled]="!newInvoiceForm.form.valid">Speichern</button>
                    </div>
                </form>
            </div>
        }

        <ng-template #templateModal>
            <div class="modal-header">
                <h4 class="modal-title pull-left">Vorlagen</h4>
                <button type="button" class="btn-close close pull-right" aria-label="Close" (click)="modalRef?.hide()">
                    <span aria-hidden="true" class="visually-hidden">&times;</span>
                </button>
            </div>
            <div class="modal-body">
                <div class="table-responsive">
                    <table class="table table-striped table-hover pointer">
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th style="width: 150px">Aktionen</th>
                        </tr>
                        </thead>
                        <tbody>
                            @for (template of templates(); track template.id) {
                                <tr>
                                    <td (click)="loadTemplate(template)">
                                        {{ template.name }}
                                    </td>
                                    <td>
                                        <button type="button" class="btn btn-sm btn-outline-primary me-2" (click)="loadTemplate(template)">
                                            <i class="fa-solid fa-download me-1"></i>Laden
                                        </button>
                                        <button type="button" class="btn btn-sm btn-outline-danger" (click)="deleteTemplate(template)">
                                            <i class="fa-solid fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            } @empty {
                                <tr>
                                    <td colspan="2" class="text-center">Keine Vorlagen gefunden</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </ng-template>

        <ng-template #pdfModal>
            <div class="modal-header">
                <h4 class="modal-title pull-left">PDF Vorschau</h4>
                <button type="button" class="btn-close close pull-right" aria-label="Close" (click)="modalRef?.hide()">
                    <span aria-hidden="true" class="visually-hidden">&times;</span>
                </button>
            </div>
            <div class="modal-body" style="height: 80vh;">
                @if (pdfUrl) {
                    <iframe [src]="pdfUrl" width="100%" height="100%" frameborder="0"></iframe>
                }
            </div>
        </ng-template>
    `,
})
export class InvoiceCustomComponent implements OnInit {

    private dataService = inject(DataService);
    private toastrService = inject(ToastrService);
    private modalService = inject(BsModalService);
    private sanitizer = inject(DomSanitizer);
    private accessService = inject(AccessService);

    protected readonly InvoiceType = InvoiceType;

    modalRef?: BsModalRef;
    pdfUrl: SafeResourceUrl | null = null;
    loading = signal(false);

    invoiceType = input.required<InvoiceType>();
    showingInvoice = signal(false);

    date = moment().format('YYYY-MM-DD');
    invoices: any[] = [];

    franchisors: any[] = [];
    franchisees: any[] = [];

    franchisorsFrom: any[] = [];
    franchiseesFrom: any[] = [];

    templates = signal<any[]>([]);

    currentInvoice!: Invoice | null;

    invoiceTypeName = computed(() => this.invoiceType() === InvoiceType.CREDIT ? 'Gutschrift' : 'Rechnung')
    paidName = computed(() => this.invoiceType() === InvoiceType.CREDIT ? 'Gezahlt am ' : 'Bezahlt am')

    labels = computed(() => {
        const name = this.invoiceTypeName();
        return {
            name,
            create: `${name} erstellen`,
            delete: `${name} löschen`,
            edit: `${name} bearbeiten`,
            send: `${name} senden`,
            show: `${name} anzeigen`,
            number: `${name}snummer`,
            header: `${name}skopf`,
            date: `${name}sdatum`,
            items: `${name}spositionen`,
            plural: `${name}en`,
            none: `Noch keine ${name}en erstellt`,
        };
    });

    ngOnInit(): void {
        this.listInvoices();
        this.loadTemplates();

        this.dataService.request("Franchisor/listForCustomInvoice").subscribe(response => {
            this.franchisors = response.franchisors;

            this.franchisorsFrom = this.franchisors.filter(franchisor => franchisor.invoiceTypes.includes(this.invoiceType()));
        });

        this.dataService.request("Franchisee/listForCustomInvoice").subscribe(response => {
            this.franchisees = response.franchisees;

            this.franchiseesFrom = this.franchisees.filter(franchisee => franchisee.invoiceTypes.includes(this.invoiceType()));
        });

        this.dataService.request("Franchisee/listForCustomInvoice").subscribe(response => {
            this.franchisees = response.franchisees;

            this.franchiseesFrom = this.franchisees.filter(franchisee => franchisee.invoiceTypes.includes(this.invoiceType()));
        });
    }

    listInvoices() {
        this.dataService.request("Invoice/getForCustomType", {
            invoiceType: this.invoiceType(),
        }).subscribe(response => {
            this.invoices = response.invoices;
        })
    }

    addInvoice() {
        this.showingInvoice.set(true);

        this.currentInvoice = {
            fromType: 'franchisor',
            fromID: '',
            toType: 'franchisee',
            toID: '',
            invoiceType: this.invoiceType(),
            header: '',
            date: moment().format('YYYY-MM-DD'),
            period: '',
            period_to: '',
            subject: '',
            items: [
                {
                    name: '',
                    price: 0,
                    vat: 20,
                }
            ],
        }
    }

    addItem() {
        if (!this.currentInvoice) return;
        this.currentInvoice.items.push({
            name: '',
            price: 0,
            vat: 20,
        });
    }

    removeItem(index: number) {
        if (!this.currentInvoice) return;
        this.currentInvoice.items.splice(index, 1);
    }

    get totalNet() {
        if (!this.currentInvoice) return 0;
        return this.currentInvoice.items.reduce((sum, item) => sum + (item.price || 0), 0);
    }

    get totalVat() {
        if (!this.currentInvoice) return 0;
        return this.currentInvoice.items.reduce((sum, item) => sum + ((item.price || 0) * (item.vat || 0) / 100), 0);
    }

    get totalGross() {
        return this.totalNet + this.totalVat;
    }

    saveInvoice() {
        if (!this.currentInvoice) return;

        this.dataService.request("Invoice/saveInvoiceCustomType", {
            invoice: this.currentInvoice,
        }).subscribe(response => {
            this.showingInvoice.set(false);
            this.listInvoices();
            this.toastrService.success(`${this.invoiceTypeName()} erfolgreich gespeichert!`);
        });
    }

    cancelAddInvoice() {
        this.showingInvoice.set(false);
    }

    createInvoice(invoice: any, template: TemplateRef<any>): void {
        this.loading.set(true);
        this.dataService.request("Invoice/createInvoiceCustomType", {
            id_invoice: invoice.id,
        }).subscribe(response => {
            this.loading.set(false);
            if (response?.pdf) {
                const byteCharacters = atob(response.pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {type: 'application/pdf'});
                const url = URL.createObjectURL(blob);

                this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                const currentModalRef = this.modalService.show(template, {class: 'modal-xl'});
                this.modalRef = currentModalRef;

                const subscription = this.modalService.onHidden.subscribe((modalRef: any) => {
                    if (modalRef === currentModalRef) {
                        this.pdfUrl = null;
                        URL.revokeObjectURL(url);
                        subscription.unsubscribe();
                    }
                });
            }

            this.listInvoices();
        });
    }

    protected deleteInvoice(invoice) {
        Swal.fire({
            title: 'Sicher?',
            text: `Die ${this.invoiceTypeName()} wird gelöscht.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request("Invoice/deleteCustom", {
                    id_invoice_custom: invoice.id,
                }).subscribe(response => {
                    this.listInvoices();
                });
            }
        });
    }

    protected editInvoice(invoice: any) {
        this.showingInvoice.set(true);

        this.currentInvoice = invoice;
    }

    protected sendInvoice(invoice: any) {
        Swal.fire({
            title: `${this.invoiceTypeName()} versenden?`,
            text: `Die ${this.invoiceTypeName()} wird an ${invoice.to_name} versandt.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Versenden',
            cancelButtonText: 'Abbrechen',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed) {
                this.dataService.request("Invoice/sendInvoiceCustomType", {
                    id_invoice: invoice.id,
                }).subscribe(response => {
                    if (response?.error) {
                        alert('Error: ' + response.error);
                        return;
                    }

                    this.listInvoices();

                    this.toastrService.success(`Die ${this.invoiceTypeName()} wurde versandt!`);
                });
            }
        });
    }

    openTemplates(template: TemplateRef<any>) {
        this.loadTemplates();
        this.modalRef = this.modalService.show(template, {class: 'modal-lg'});
    }

    loadTemplates() {
        this.dataService.request("Invoice/loadInvoiceCustomTemplates").subscribe(response => {
            this.templates.set(response.templates.filter(t => t.invoice_type === this.invoiceType()));
        });
    }

    saveTemplate() {
        if (!this.currentInvoice) return;

        Swal.fire({
            title: 'Als Vorlage speichern',
            input: 'text',
            inputLabel: 'Name der Vorlage',
            inputValue: this.currentInvoice.header,
            showCancelButton: true,
            confirmButtonText: 'Speichern',
            cancelButtonText: 'Abbrechen',
        }).then(result => {
            if (result.isConfirmed && result.value) {
                const invoice = {
                    ...this.currentInvoice,
                    name: result.value,
                    invoiceType: this.invoiceType()
                };

                this.dataService.request("Invoice/saveInvoiceCustomTemplate", {
                    invoice: invoice
                }).subscribe(() => {
                    this.toastrService.success("Vorlage erfolgreich gespeichert!");
                    this.loadTemplates();
                });
            }
        });
    }

    loadTemplate(template: any) {
        this.showingInvoice.set(true);
        this.currentInvoice = {
            fromType: template.fromType,
            fromID: template.fromID,
            toType: template.toType,
            toID: template.toID,
            invoiceType: template.invoice_type,
            header: template.header,
            date: moment().format('YYYY-MM-DD'),
            period: template.period,
            period_to: template.period_to,
            subject: template.subject,
            items: JSON.parse(template.items)
        };
        this.modalRef?.hide();
    }

    deleteTemplate(template: any) {
        Swal.fire({
            title: 'Vorlage löschen?',
            text: `Die Vorlage "${template.name}" wird gelöscht.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Löschen',
            cancelButtonText: 'Abbrechen'
        }).then(result => {
            if (result.value) {
                this.dataService.request("Invoice/deleteInvoiceCustomTemplate", {
                    id_invoice_custom_template: template.id
                }).subscribe(() => {
                    this.loadTemplates();
                    this.toastrService.success("Vorlage wurde gelöscht.");
                });
            }
        });
    }

    protected loadInvoiceType() {
        this.dataService.request("InvoiceType/find", {
            invoice_type: this.invoiceType(),
            id_franchisor: this.currentInvoice.fromType === 'franchisor' ? this.currentInvoice.fromID : null,
            id_franchisee: this.currentInvoice.fromType === 'franchisee' ? this.currentInvoice.fromID : null
        }).subscribe((response) => {
            const type = response.type;

            if (type.header != null) {
                this.currentInvoice.header = type.header;
            }

            if (type.subject != null) {
                this.currentInvoice.subject = type.subject;
            }
        });
    }

    protected resetFromID() {
        this.currentInvoice.fromID = '';
    }

    protected resetToID() {
        this.currentInvoice.toID = '';
    }

    protected setPaidDate(invoice: any) {
        Swal.fire({
            title: this.paidName(),
            input: 'date',
            inputValue: moment().format('YYYY-MM-DD'),
            showCancelButton: true,
            confirmButtonText: 'Speichern',
            cancelButtonText: 'Abbrechen',
            confirmButtonColor: this.accessService.client.primaryColor,
            cancelButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                this.loading.set(true);
                this.dataService.request("Invoice/setPaidDateCustom", {
                    id: invoice.id,
                    paid_at: result.value
                }).subscribe(response => {
                    this.loading.set(false);
                    this.toastrService.success("Datum wurde gespeichert!");
                    this.listInvoices();
                });
            }
        });
    }
}
